import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeUp, fadeUpSmall, staggerContainer, cardItem, defaultViewport } from '../lib/motion';

const stats = [
  { value: '500+', label: 'Devices repaired' },
  { value: '98%', label: 'Customer satisfaction' },
  { value: '24h', label: 'Average turnaround' },
  { value: '10+', label: 'Years of experience' },
];

const services = [
  {
    icon: '🖨️',
    title: 'Printer Repair',
    description:
      'In-shop and doorstep repair for all major brands — HP, Canon, Epson, Brother, and more.',
    tags: ['All brands', 'Doorstep available'],
    link: '/book-service',
  },
  {
    icon: '💻',
    title: 'Computer Solutions',
    description:
      'Diagnostics, hardware upgrades, OS reinstalls, virus removal, and performance tuning.',
    tags: ['Laptops & Desktops', 'Windows & Mac'],
    link: '/book-service',
  },
  {
    icon: '🛒',
    title: 'Quality Products',
    description:
      'Genuine ink cartridges, toner, cables, peripherals, and accessories at competitive prices.',
    tags: ['Genuine parts', 'Warranty included'],
    link: '/products',
  },
];

const steps = [
  { num: '01', title: 'Book online', desc: 'Fill in your details and describe the issue in minutes.' },
  { num: '02', title: 'We diagnose', desc: 'Our engineer assesses the problem and confirms a quote.' },
  { num: '03', title: 'We fix it', desc: 'Fast, reliable repair — in-shop or at your doorstep.' },
  { num: '04', title: 'Pick up & go', desc: 'Your device returned, tested, and ready to use.' },
];

const faqs = [
  { q: 'Do you offer doorstep service?', a: 'Yes — select "Doorstep service" when booking and we\'ll come to you.' },
  { q: 'Which printer brands do you support?', a: 'We service all major brands including HP, Canon, Epson, Brother, Ricoh, and Samsung.' },
  { q: 'How long does a typical repair take?', a: 'Most repairs are completed within 24 hours. Complex jobs may take up to 3 business days.' },
  { q: 'Is there a diagnosis fee?', a: 'Basic diagnosis is free. You only pay if you proceed with the repair.' },
];

const Home = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Hero ─────────────────────────────────────────── */}
      {/* -mt-16 cancels out the Navbar's h-16 spacer, pulling this section up
          so it renders behind the fixed, transparent navbar instead of below it. */}
      <section className="relative overflow-hidden bg-gray-900 -mt-16">
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            backgroundImage: `url('https://media.istockphoto.com/id/1327558467/photo/office-worker-prints-paper-on-multifunction-laser-printer-copy-print-scan-and-fax-machine-in.jpg?s=612x612&w=0&k=20&c=pgiDapgy2oXtzZV7AGdhb4i27VxcP4e-6xBXLfdPTE8=')`,
          }}
        />
        {/* Dark gradient overlay — strongest at top so the navbar always has contrast,
            and keeps the white hero text/buttons legible over the photo. */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />

        <motion.div
          className="relative z-10 max-w-3xl mx-auto px-6 pt-32 pb-20 text-center"
          variants={staggerContainer}
          initial="hidden"
          animate="show"
        >
          <motion.span
            variants={fadeUpSmall}
            className="inline-block bg-white/10 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-5 border border-white/20"
          >
            Purnia's trusted repair centre
          </motion.span>
          <motion.h1
            variants={fadeUp}
            className="text-4xl md:text-5xl font-bold text-white leading-tight mb-5"
          >
            Printer & computer repair,<br />
            <span className="text-blue-400">done right.</span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="text-lg text-gray-200 mb-8 max-w-xl mx-auto"
          >
            MV Computers offers fast, honest, and affordable repair services — at our shop or at your doorstep.
          </motion.p>
          <motion.div
            variants={fadeUp}
            className="flex flex-col items-center gap-4"
          >
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Link
                to="/book-service"
                className="block bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm px-8 py-3.5 rounded-xl transition-colors"
              >
                Book a service
              </Link>
            </motion.div>
            <Link
              to="/products"
              className="text-sm text-gray-300 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors"
            >
              Or browse our products →
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Stats bar ────────────────────────────────────── */}
      <motion.section
        className="bg-white border-b border-gray-100"
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={defaultViewport}
      >
        <div className="max-w-4xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {stats.map((s) => (
            <motion.div key={s.label} variants={cardItem}>
              <p className="text-2xl font-bold text-blue-600">{s.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* ── Services ─────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          className="mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">What we do</p>
          <h2 className="text-2xl font-bold text-gray-900">Services</h2>
        </motion.div>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          {services.map((svc) => (
            <motion.div key={svc.title} variants={cardItem} whileHover={{ y: -4 }}>
              <Link
                to={svc.link}
                className="group bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-blue-200 transition-shadow flex flex-col h-full"
              >
                <span className="text-3xl mb-3">{svc.icon}</span>
                <h3 className="font-semibold text-gray-900 mb-1">{svc.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed flex-1">{svc.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {svc.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-xs font-medium text-blue-600 mt-4 group-hover:underline">
                  Book now →
                </p>
              </Link>
            </motion.div>
          ))}
        </motion.div>
        <motion.div
          className="mt-8 text-center"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          <Link
            to="/services"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors"
          >
            View all services →
          </Link>
        </motion.div>
      </section>

      {/* ── Testimonials ────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 py-16 bg-gray-50 border-t border-gray-100">
        <motion.div
          className="mb-10 text-center md:text-left"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Reviews</p>
          <h2 className="text-2xl font-bold text-gray-900">What our customers say</h2>
        </motion.div>
        
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          {[
            {
              stars: 5,
              text: "Fantastic doorstep service. Manoj resolved our office network printer issue in Purnea within an hour. Excellent response time!",
              name: "Rajesh Ranjan",
              role: "Office Manager, Purnea"
            },
            {
              stars: 5,
              text: "Quick refilling and genuine parts. I regularly get my Canon cartridges refilled here. Pricing is fair and transparent.",
              name: "Amit Kumar",
              role: "Local Business Owner"
            },
            {
              stars: 5,
              text: "Highly recommended repair shop. Laptop SSD upgrade was done in 2 hours. It feels brand new now. Honest advice.",
              name: "Neha Sinha",
              role: "Freelance Designer"
            }
          ].map((t, idx) => (
            <motion.div
              key={idx}
              variants={cardItem}
              className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm flex flex-col justify-between"
            >
              <div>
                <div className="flex text-amber-400 gap-0.5 mb-3.5">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <span key={i} className="text-sm">★</span>
                  ))}
                </div>
                <p className="text-sm text-gray-600 italic leading-relaxed mb-4">
                  "{t.text}"
                </p>
              </div>
              <div className="flex items-center gap-3 pt-3 border-t border-gray-50 mt-auto">
                <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 font-bold flex items-center justify-center text-xs">
                  {t.name.split(' ').map(n => n[0]).join('')}
                </span>
                <div>
                  <h4 className="text-xs font-bold text-gray-900">{t.name}</h4>
                  <p className="text-[10px] text-gray-400 mt-0.5">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      {/* Placed before "How it works" so common objections (doorstep?
          which brands? diagnosis fee?) get cleared before the visitor
          reads through the booking process. */}
      <section className="max-w-2xl mx-auto px-6 py-16">
        <motion.div
          className="mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Got questions?</p>
          <h2 className="text-2xl font-bold text-gray-900">Frequently asked</h2>
        </motion.div>
        <motion.div
          className="flex flex-col gap-2"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              variants={cardItem}
              className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm"
            >
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors"
              >
                {faq.q}
                <motion.span
                  animate={{ rotate: openFaq === i ? 45 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-gray-400 text-lg"
                >
                  +
                </motion.span>
              </button>
              <motion.div
                initial={false}
                animate={{
                  height: openFaq === i ? 'auto' : 0,
                  opacity: openFaq === i ? 1 : 0,
                }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-4 text-sm text-gray-500 leading-relaxed border-t border-gray-100 pt-3">
                  {faq.a}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── How it works ─────────────────────────────────── */}
      <section className="bg-white border-y border-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            className="mb-10"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={defaultViewport}
          >
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Simple process</p>
            <h2 className="text-2xl font-bold text-gray-900">How it works</h2>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={defaultViewport}
          >
            {steps.map((step, i) => (
              <motion.div key={step.num} variants={cardItem} className="relative">
                <p className="text-3xl font-bold text-blue-100 mb-2">{step.num}</p>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{step.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                {i < steps.length - 1 && (
                  <span className="hidden md:block absolute top-4 right-0 translate-x-1/2 text-gray-200 text-xl">→</span>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA banner ───────────────────────────────────── */}
      <motion.section
        className="bg-blue-600 py-14"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={defaultViewport}
      >
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Ready to get your device fixed?</h2>
          <p className="text-blue-200 text-sm mb-7">Book in under 2 minutes. No jargon, no hidden fees.</p>
          <motion.div className="inline-block" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Link
              to="/book-service"
              className="block bg-white text-blue-700 font-semibold text-sm px-8 py-3 rounded-xl hover:bg-blue-50 transition-colors"
            >
              Book a service
            </Link>
          </motion.div>
        </div>
      </motion.section>

    </div>
  );
};

export default Home;